import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, Alert, Button, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const STREAMLIT_PROXY_URL = '/api/gl-data-generator/';

const GLDataGenerator = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    // Check if Streamlit is reachable via the proxy
    const checkHealth = async () => {
      try {
        const response = await fetch(STREAMLIT_PROXY_URL, { method: 'GET' });
        if (response.ok) {
          setLoading(false);
          setError(false);
        } else {
          setLoading(false);
          setError(true);
        }
      } catch (err) {
        setLoading(false);
        setError(true);
      }
    };
    checkHealth();
  }, []);

  const handleRetry = () => {
    setLoading(true);
    setError(false);
    // Force iframe reload
    if (iframeRef.current) {
      iframeRef.current.src = STREAMLIT_PROXY_URL;
    }
    // Re-check health
    setTimeout(async () => {
      try {
        const response = await fetch(STREAMLIT_PROXY_URL, { method: 'GET' });
        if (response.ok) {
          setLoading(false);
          setError(false);
        } else {
          setLoading(false);
          setError(true);
        }
      } catch {
        setLoading(false);
        setError(true);
      }
    }, 1000);
  };

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 120px)',
          gap: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            GL Data Generator
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The GL Data Generator service is not running. Please ensure the Streamlit
            server has been started (port 8501).
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Start it with: <code>./start_gl_data_generator.sh</code>
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
          >
            Retry Connection
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          px: 1,
        }}
      >
        <Typography variant="h5">GL Data Generator</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
          >
            Refresh
          </Button>
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(STREAMLIT_PROXY_URL, '_blank')}
          >
            Open in New Tab
          </Button>
        </Box>
      </Box>
      <Box sx={{ flex: 1, position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading GL Data Generator...</Typography>
          </Box>
        )}
        <iframe
          ref={iframeRef}
          src={STREAMLIT_PROXY_URL}
          title="GL Data Generator"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '8px',
          }}
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setError(true);
          }}
        />
      </Box>
    </Box>
  );
};

export default GLDataGenerator;
